import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import type { UserPermission } from 'services/permissions/entities/UserPermissions';
import type { UserPermissionsRepository } from 'services/permissions/repositories/UserPermissionsRepository';
import { QueryKeys } from 'utils/queryKeys';

interface Props {
  children: ReactNode;
  repository: UserPermissionsRepository;
}

type ProviderValue = {
  userPermissions: UserPermission[];
};

export const PermissionsContext = createContext<ProviderValue>({ userPermissions: [] });
PermissionsContext.displayName = 'PermissionsContext';

const PermissionsProvider: React.FunctionComponent<Props> = ({ children, repository }) => {
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);

  const { data } = useQuery({
    queryKey: QueryKeys.currentUserPermissions,
    queryFn: () => repository.getUserPermissions(),
  });

  useEffect(() => {
    setUserPermissions(data ?? []);
  }, [data]);

  const value = useMemo(
    () => ({
      userPermissions,
    }),
    [userPermissions]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissionsContextValue = () => {
  const value = useContext(PermissionsContext);
  if (!value) {
    throw Error('usePermissionsContextValue hook must be used inside PermissionsProvider');
  }

  return value;
};

export default PermissionsProvider;
